import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../styles/dashboard.css';
import '../styles/students.css';
import '../styles/counselors.css';
import '../styles/applications.css';
import '../styles/visa.css';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { ViewModeWrap } from '../components/ViewRecordModal';
import { useAuth } from '../context/AuthContext';
import VisaFormFields from '../components/VisaFormFields';
import VisaDocumentSlots from '../components/VisaDocumentSlots';
import { emptyVisaForm, mapVisaFromApi } from '../constants/visaFormOptions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPassport, faSave, faTimes, faArrowLeft, faPrint } from '@fortawesome/free-solid-svg-icons';

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

export default function EditVisa({ recordId, viewOnly = false } = {}) {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const id = recordId || paramId;
  const { user } = useAuth();
  const isStudentPortal = user?.role === 'client';
  const isViewOnly = viewOnly || isStudentPortal;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(emptyVisaForm());
  const [visaRef, setVisaRef] = useState('');
  const [countries, setCountries] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [pendingByType, setPendingByType] = useState({});
  const [deletedDocIds, setDeletedDocIds] = useState([]);

  useEffect(() => {
    fetchCountries();
    fetchCounselors();
    fetchVisa();
  }, [id]);

  const fetchCountries = async () => {
    try {
      const res = await fetch(`${API_URL}/countries`);
      const data = await res.json();
      if (data.success) setCountries(data.countries.filter((c) => c.status === 'Active'));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCounselors = async () => {
    try {
      const res = await fetch(`${API_URL}/counselors`);
      const data = await res.json();
      if (data.success) setCounselors(data.counselors || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchVisa = async () => {
    try {
      setFetching(true);
      const res = await fetch(`${API_URL}/visas/${id}`);
      const data = await res.json();
      if (data.success) {
        const visa = data.visa;
        if (isStudentPortal) {
          const emailMatch = String(visa.applicant_email || visa.student_email || '').trim().toLowerCase()
            === String(user?.email || '').trim().toLowerCase();
          const idMatch = String(visa.student_id || '') === String(user?.id || '');
          if (!emailMatch && !idMatch) {
            alert('You can only edit your own visa records.');
            navigate('/my-visas', { replace: true });
            return;
          }
        }
        setFormData(mapVisaFromApi(visa));
        setVisaRef(visa.visa_id || '');
        setDocuments(data.documents || []);
        setPendingByType({});
        setDeletedDocIds([]);
      } else {
        alert('Visa not found');
        if (!isViewOnly) navigate(isStudentPortal ? '/my-visas' : '/visa');
      }
    } catch (e) {
      alert('Failed to load visa');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    if (isViewOnly) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const queueFile = (type, file) => {
    if (isViewOnly || !file) return;
    setPendingByType((prev) => ({
      ...prev,
      [type]: [...(prev[type] || []), file],
    }));
  };

  const removePending = (type, index) => {
    setPendingByType((prev) => ({
      ...prev,
      [type]: (prev[type] || []).filter((_, i) => i !== index),
    }));
  };

  const handleDeleteDocument = (docId) => {
    setDeletedDocIds((prev) => (prev.includes(docId) ? prev : [...prev, docId]));
  };

  const uploadPendingDocuments = async () => {
    const entries = Object.entries(pendingByType);
    for (const [documentType, files] of entries) {
      for (const file of files) {
        const fd = new FormData();
        fd.append('document', file);
        fd.append('documentType', documentType);
        await fetch(`${API_URL}/visas/${id}/documents`, { method: 'POST', body: fd });
      }
    }
  };

  const deleteMarkedDocuments = async () => {
    for (const docId of deletedDocIds) {
      await fetch(`${API_URL}/visas/${id}/documents/${docId}`, { method: 'DELETE' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViewOnly) return;
    if (!formData.applicantName || !formData.countryId || !formData.visaType || !formData.visaStatus) {
      setError('Please fill in all required fields.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_URL}/visas/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        await deleteMarkedDocuments();
        await uploadPendingDocuments();
        alert('Visa updated successfully!');
        navigate(isStudentPortal ? '/my-visas' : '/visa');
      } else {
        setError(data.message || 'Failed to update visa');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (e) {
      setError('Network error. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <ViewModeWrap viewOnly={viewOnly}>
        <Sidebar />
        <div className="main-content" id="mainContent">
          <Header />
          <div style={{ textAlign: 'center', padding: '40px' }}><p>Loading visa...</p></div>
        </div>
      </ViewModeWrap>
    );
  }

  return (
    <ViewModeWrap viewOnly={viewOnly}>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="students-container">
          <div className="students-header no-print">
            <div className="header-left">
              <h1>
                <FontAwesomeIcon icon={faPassport} className="page-icon" />
                {isViewOnly ? 'Visa Details' : 'Edit Visa'}
              </h1>
            </div>
            <div className="header-right">
              <button type="button" className="btn-secondary" onClick={() => window.print()} style={{ marginRight: '10px' }}>
                <FontAwesomeIcon icon={faPrint} /> Print
              </button>
              <Link to={isStudentPortal ? '/my-visas' : '/visa'} className="btn-secondary">
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to Visas
              </Link>
            </div>
          </div>

          {error && (
            <div className="visa-enquiry-alert visa-enquiry-alert-error no-print">{error}</div>
          )}

          {visaRef && (
            <div className="student-id-card">
              <div className="student-id-info">
                <span className="id-label">Visa ID:</span>
                <span className="student-id-display">{visaRef}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className={`counselor-form student-form ${isViewOnly ? 'form-view-only' : ''}`}>
            <VisaFormFields
              data={formData}
              onChange={handleChange}
              countries={countries}
              counselors={counselors}
              disabled={isViewOnly}
            />
            <div className="no-print">
              <VisaDocumentSlots
                documents={documents.filter((d) => !deletedDocIds.includes(d.id))}
                pendingByType={pendingByType}
                onQueueFile={queueFile}
                onRemovePending={removePending}
                onDelete={handleDeleteDocument}
                showPending
                viewOnly={isViewOnly}
              />
            </div>
            {!isViewOnly && (
              <div className="form-actions no-print">
                <button type="button" className="btn-secondary" onClick={() => navigate(isStudentPortal ? '/my-visas' : '/visa')} disabled={loading}>
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  <FontAwesomeIcon icon={faSave} /> {loading ? 'Saving...' : 'Update Visa'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </ViewModeWrap>
  );
}
